import {
  addPackageByGroupId,
  getPackagesByGroupId,
} from "../../../../Redux/Groups/GroupsActions";
import validator, { ValidationField } from "../../../../Util/validator";
import { AppDispatch, RootState } from "../../../../Redux/store";
import Select, { components, SingleValue } from "react-select";
import toastHelper from "../../../../Util/toastHelper";
import { useDispatch, useSelector } from "react-redux";
import Loading from "../../../Common/Loading/Loading";
import { RiDeleteBinLine } from "react-icons/ri";
import styles from "./AddPackage.module.scss";
import React, { useState } from "react";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  groupId: string;
}

interface Amenity {
  amenityId: number;
  name: string;
  status: string;
  image: string;
  requiredBooking: boolean;
}

interface SelectOption {
  value: number;
  label: string;
  data: Amenity;
}

function AddPackage(props: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const { setIsOpen, groupId } = props;
  const { addPackageByGroupIdLoading, getAmenitiesByGroupIdData } = useSelector(
    (state: RootState) => state.groups,
  );
  const [packageName, setPackageName] = useState("");
  const [packageDescription, setPackageDescription] = useState("");
  const [status, setStatus] = useState("true");
  const [selectedAmenities, setSelectedAmenities] = useState<Amenity[]>([]);
  const [inputValue, setInputValue] = useState<SelectOption | null>(null);
  const [price, setPrice] = useState("");
  const [freeTrial, setFreeTrial] = useState(false);
  const [isFamily, setIsFamily] = useState(false);
  const [noOfMember, setNoOfMember] = useState("");
  const [discountPrice, setDiscountPrice] = useState("");
  const [allowTo, setAllowTo] = useState("all");
  const [errors, setErrors] = useState<{
    packageName: string;
    packageDescription: string;
    amenityIds: string;
    status: string;
    price: string;
    noOfMember: string;
    discountPrice: string;
  }>({
    packageName: "",
    packageDescription: "",
    amenityIds: "",
    status: "",
    price: "",
    noOfMember: "",
    discountPrice: "",
  });

  const options = getAmenitiesByGroupIdData
    ?.filter((item) => item?.amenityTypeName?.toLowerCase() !== "parking")
    ?.map((item) => ({
      value: item.amenityId,
      label: item.name,
      data: item,
    }));

  console.log("options", getAmenitiesByGroupIdData);

  const handleChange = (selectedOption: SingleValue<SelectOption>) => {
    if (!selectedOption) return;

    const alreadyAdded = selectedAmenities.find(
      (item: Amenity) => item.amenityId === selectedOption.data.amenityId,
    );

    if (!alreadyAdded) {
      setSelectedAmenities((prev) => [...prev, { ...selectedOption.data }]);
    }

    setInputValue(null);
  };

  const CustomOption = (props: any) => {
    return (
      <components.Option {...props} className={styles.dropdownItem}>
        {props?.data?.label}
      </components.Option>
    );
  };

  const deleteAmenityHandler = (amenityId: number) => {
    setSelectedAmenities((prev) =>
      prev.filter((item) => item.amenityId !== amenityId),
    );
  };

  const addPackageHandler = () => {
    let fieldsArray = [
      {
        key: "packageName",
        value: packageName,
        type: "text",
      },

      {
        key: "packageDescription",
        value: packageDescription,
        type: "text",
      },
      {
        key: "amenityIds",
        value: selectedAmenities.map((item) => item.amenityId),
        type: "array",
      },
      {
        key: "status",
        value: status,
        type: "text",
      },
      {
        key: "price",
        value: price,
        type: "text",
      },
    ];

    if (isFamily) {
      fieldsArray.push({
        key: "noofMembers",
        value: noOfMember.toString(),
        type: "numberOnly",
      });
    }

    const { errors: fieldsErrors, isValid } = validator(
      fieldsArray as ValidationField[],
    );

    setErrors({ ...errors, ...fieldsErrors });
    if (isValid) {
      if (!addPackageByGroupIdLoading) {
        const successHandler = () => {
          setIsOpen(false);
          if (groupId) {
            dispatch(
              getPackagesByGroupId({
                groupId: groupId,
              }),
            );
          }
          toastHelper.success("Package added successfully");
        };

        if (groupId) {
          dispatch(
            addPackageByGroupId({
              packageName,
              packageDescription: packageDescription,
              groupId: Number(groupId),
              status: status === "true" ? true : false,
              amenityIds: selectedAmenities.map((item) => item?.amenityId),
              price: Number(price),
              isFreeMonth: freeTrial,
              isFamily: isFamily,
              noOfMember: noOfMember ? noOfMember : "0",
              discountRate: discountPrice,
              isMember:
                allowTo === "member" || allowTo === "all" ? true : false,
              isNonMember:
                allowTo === "non-member" || allowTo === "all" ? true : false,
              successHandler,
            }),
          );
        }
      }
    }
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.headingContainer}>
        <h1 className={styles.heading}>Add Package</h1>
      </div>
      <form className={styles.formContainer}>
        <div className={styles.inputContainer}>
          <p className={styles.label}>Package Name</p>
          <input
            type="text"
            className={styles.input}
            value={packageName}
            onChange={(e) => setPackageName(e.target.value)}
          />
          <p className={styles.error}>{errors.packageName}</p>
        </div>
        <div className={styles.textAreaContainer}>
          <p className={styles.label}>Package Description</p>
          <textarea
            className={styles.textArea}
            value={packageDescription}
            onChange={(e) => setPackageDescription(e.target.value)}
            rows={4}
          />
          <p className={styles.error}>{errors.packageDescription}</p>
        </div>
        <div className={styles.inputContainer}>
          <p className={styles.label}>Status</p>
          <select
            className={styles.input}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <p className={styles.error}>{errors.status}</p>
        </div>
        <div className={styles.doubleInputContainer}>
          <div className={styles.priceInputContainer}>
            <p className={styles.label}>Price</p>
            <div className={styles.inputContainer}>
              <p className={styles.currency}>$</p>
              <input
                type="text"
                className={styles.input}
                value={price}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d{0,2}$/.test(value)) {
                    setPrice(value);
                  }
                }}
              />
            </div>
            <p className={styles.error}>{errors.price}</p>
          </div>
          <div className={styles.priceInputContainer}>
            <p className={styles.label}>Discount Price</p>
            <div className={styles.inputContainer}>
              <p className={styles.currency}>$</p>
              <input
                type="text"
                className={styles.input}
                value={discountPrice}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d{0,2}$/.test(value)) {
                    setDiscountPrice(value);
                  }
                }}
              />
            </div>

            <p className={styles.error}>{errors.discountPrice}</p>
          </div>
        </div>
        <div className={styles.selectorContainer}>
          <p className={styles.label}>Amenities</p>
          <div className={styles.packageListContainer}>
            {selectedAmenities.map((item, i: number) => (
              <div className={styles.packageItem} key={i}>
                <div className={styles.labelContainer}>
                  <p className={styles.label}>{item.name}</p>
                </div>
                <div className={styles.rightContainer}></div>
                <div className={styles.iconContainer}>
                  <RiDeleteBinLine
                    className={styles.icon}
                    onClick={() => deleteAmenityHandler(item.amenityId)}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className={styles.searchAmenitiesSelect}>
            <Select
              options={options}
              value={inputValue}
              onChange={handleChange}
              onInputChange={() => {}}
              placeholder="Search amenities..."
              getOptionLabel={(e) => e.label}
              // className={styles.searchAmenitiesSelect}
              classNamePrefix="my-select"
              components={{
                Option: CustomOption,
              }}
              formatOptionLabel={(e) => (
                <div className={styles.AmenitiesLabel}>{e.label}</div>
              )}
            />
            <p className={styles.error}>{errors.amenityIds}</p>
          </div>
        </div>
        <div className={styles.checkboxWrapperContainer}>
          <div className={styles.checkboxContainer}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={freeTrial}
              onChange={(e) => setFreeTrial(e.target.checked)}
            />
            <p className={styles.label}>Free Trial</p>
          </div>
          <div className={styles.checkboxContainer}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={isFamily}
              onChange={(e) => setIsFamily(e.target.checked)}
            />
            <p className={styles.label}>Is Family Package</p>
          </div>
        </div>
        <div className={styles.priceInputContainer}>
          <p className={styles.label}>No of Members</p>
          <div className={styles.inputContainer}>
            <input
              type="text"
              className={styles.input}
              value={noOfMember}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*$/.test(value)) {
                  setNoOfMember(value);
                }
              }}
            />
          </div>
          <p className={styles.error}>{errors.noOfMember}</p>
        </div>
        <div className={styles.inputContainer}>
          <p className={styles.label}>Allowed To</p>
          <div className={styles.checkboxesContainer}>
            <div className={styles.checkboxContainer}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={allowTo === "all"}
                onChange={(e) => setAllowTo(e.target.checked ? "all" : "")}
              />
              <p className={styles.label}>All</p>
            </div>
            <div className={styles.checkboxContainer}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={allowTo === "members"}
                onChange={(e) => setAllowTo(e.target.checked ? "members" : "")}
              />
              <p className={styles.label}>Members</p>
            </div>
            <div className={styles.checkboxContainer}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={allowTo === "non-members"}
                onChange={(e) =>
                  setAllowTo(e.target.checked ? "non-members" : "")
                }
              />
              <p className={styles.label}>Non Members</p>
            </div>
          </div>
        </div>
        {addPackageByGroupIdLoading && <Loading />}
      </form>

      <div className={styles.buttonContainer}>
        <button className={styles.button} onClick={addPackageHandler}>
          Add
        </button>
        <button className={styles.cancelBtn} onClick={() => setIsOpen(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default AddPackage;
